import React, { useState, useEffect } from 'react';
import { Select, DatePicker, Button, Table, message, Space } from 'antd';
import moment from 'moment';
import { collection, query, getDocs, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../firebase/fire';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import LoadingOverlay from './LoadingOverlay';
import { FaClock, FaCheck, FaFileExcel, FaFilePdf } from 'react-icons/fa';

const { Option } = Select;

const MemberReport = ({ theme = 'light' }) => {
    const [members, setMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMemberName, setSelectedMemberName] = useState('');
    const [formattedDateRange, setFormattedDateRange] = useState('');
    const [totalLateMinutes, setTotalLateMinutes] = useState(0);
    const [auditLogs, setAuditLogs] = useState([]);


    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            const membersRef = collection(db, 'members');
            const membersSnapshot = await getDocs(membersRef);
            const membersData = membersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Sort the members array by name (guard against missing names)
            const sortedMembersData = membersData.sort((a, b) => {
                const nameA = (a && a.name) ? String(a.name) : '';
                const nameB = (b && b.name) ? String(b.name) : '';
                return nameA.localeCompare(nameB);
            });

            setMembers(sortedMembersData);
        } catch (error) {
            console.error("Error fetching members:", error);
            message.error('Failed to fetch members');
        }
    };

    const fetchAttendanceData = async (memberId, startDate, endDate) => {
        setLoading(true);
        try {
            const rangeStart = moment(startDate).startOf('day');
            const rangeEnd = moment(endDate).endOf('day');
            let totalLate = 0;

            const classesQuery = query(collection(db, 'classSchedule'));
            const querySnapshot = await getDocs(classesQuery);

            const report = [];

            querySnapshot.forEach((doc) => {
                const classData = doc.data();
                const attendance = classData.attendance || {};

                Object.entries(attendance).forEach(([date, attendanceList]) => {
                    const attendanceDate = moment(date);
                    if (attendanceDate.isBetween(rangeStart, rangeEnd, null, '[]')) {
                        const memberAttendance = attendanceList.find(a => a.userId === memberId);
                        if (memberAttendance) {
                            const scheduledStartTime = moment(`${date} ${classData.startTime}`, 'YYYY-MM-DD HH:mm');
                            const scheduledEndTime = moment(`${date} ${classData.endTime}`, 'YYYY-MM-DD HH:mm');
                            const attendanceTime = moment(memberAttendance.time);


                            const timeDiffMinutes = attendanceTime.diff(scheduledStartTime, 'minutes');
                            const timeDifference = timeDiffMinutes > 0 ? timeDiffMinutes : 0;
                            let actualDuration;
                            const durationMinutes = scheduledEndTime.diff(scheduledStartTime, 'minutes');
                            if (timeDiffMinutes < 0) {
                                actualDuration = durationMinutes; // On time or early
                            } else {
                                actualDuration = Math.max(0, scheduledEndTime.diff(attendanceTime, 'minutes')); // Late
                            }


                            report.push({
                                key: `${doc.id}-${date}`,
                                date: attendanceDate.format('YYYY-MM-DD'),
                                className: classData.className,
                                scheduledStartTime: scheduledStartTime.format('HH:mm'),
                                scheduledEndTime: scheduledEndTime.format('HH:mm'),
                                scheduledDuration: `${durationMinutes} mins`,
                                actualLoginTime: attendanceTime.format('HH:mm'),
                                timeDifference: timeDifference,
                                actualDuration: `${actualDuration} mins`,
                            });
                            totalLate += timeDifference;
                        }
                    }
                });
            });
            setReportData(report.sort((a, b) => moment(b.date).diff(moment(a.date))));
            //setReportData(report);
            setFormattedDateRange(
                startDate && endDate
                    ? `${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`
                    : ''
            );
            setTotalLateMinutes(totalLate);

        } catch (error) {
            console.error("Error fetching attendance data:", error);
            message.error('Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const ReportHeader = () => (
        <div style={{ marginBottom: 16 }}>
            <h2 style={{ fontWeight: 700, fontSize: 18 }}>Member: {selectedMemberName}</h2>
            <h2 style={{ fontWeight: 700, fontSize: 16 }}>Period: {formattedDateRange}</h2>
        </div>
    );

    const handleMemberSelect = (value) => {
        setSelectedMember(value);
        const selectedMemberData = members.find(member => member.id === value);
        setSelectedMemberName(selectedMemberData ? selectedMemberData.name : '');
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    const generateReport = async () => {
        if (!selectedMember || !startDate || !endDate) {
            message.warning('Please select a member and both start and end dates');
            return;
        }

        await fetchAttendanceData(
            selectedMember,
            startDate.toDate(),
            endDate.toDate()
        );
        const logs = await fetchAuditLogs(selectedMember);
        setAuditLogs(logs);
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 100,
        },
        {
            title: 'Class Name',
            dataIndex: 'className',
            key: 'className',
            width: 120,
        },
        {
            title: 'Scheduled Start Time',
            dataIndex: 'scheduledStartTime',
            key: 'scheduledStartTime',
            width: 120,
        },
        {
            title: 'Scheduled End Time',
            dataIndex: 'scheduledEndTime',
            key: 'scheduledEndTime',
            width: 120,
        },
        {
            title: 'Scheduled Duration',
            dataIndex: 'scheduledDuration',
            key: 'scheduledDuration',
            width: 120,
        },
        {
            title: 'Actual Login Time',
            dataIndex: 'actualLoginTime',
            key: 'actualLoginTime',
            width: 120,
        },
        {
            title: 'Time Difference',
            dataIndex: 'timeDifference',
            key: 'timeDifference',
            width: 120,
            render: (timeDifference) => {
                if (timeDifference > 0) {
                    return (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            <FaClock /> {timeDifference} min late
                        </span>
                    );
                } else {
                    return (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            <FaCheck /> On Time
                        </span>
                    );
                }
            }
        },
        {
            title: 'Actual Duration',
            dataIndex: 'actualDuration',
            key: 'actualDuration',
            width: 120,
        },
    ];

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text(`Member: ${selectedMemberName}`, 14, 15);
        doc.text(`Period: ${formattedDateRange}`, 14, 25);

        doc.autoTable({
            startY: 35,
            head: [columns.map(col => col.title)],
            body: [
                ...reportData.map(item => columns.map(col => item[col.dataIndex])),
                ['Total', '', '', '', totals.scheduledHours, '', '', totals.actualHours]
            ],
        });

        doc.save(`member_report_${selectedMemberName}.pdf`);
    };

    const exportToExcel = () => {
        // Create worksheet from report data
        const ws = XLSX.utils.json_to_sheet([
            { A: `Member: ${selectedMemberName}` }, // Add coach name
            { A: `Period: ${formattedDateRange}` }, // Add time period
            {},  // Empty row for spacing
            { A: 'Date', B: 'Class Name', C: 'Scheduled Start Time', D: 'Scheduled End Time', E: 'Scheduled Duration', F: 'Actual Login Time', G: 'Time Difference', H: 'Actual Duration' }, // Headers
            ...reportData.map(item => ({
                A: item.date,
                B: item.className,
                C: item.scheduledStartTime,
                D: item.scheduledEndTime,
                E: item.scheduledDuration,
                F: item.actualLoginTime,
                G: item.timeDifference,
                H: item.actualDuration
            }))
        ]);

        // Create workbook and append worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Member Report");

        // Add total row with proper alignment
        const totalRow = [
            { v: 'Total', t: 's' }, '', '', '', totals.scheduledHours, '', '', totals.actualHours
        ];
        XLSX.utils.sheet_add_aoa(ws, [totalRow], { origin: -1 });

        // Write to file
        XLSX.writeFile(wb, "member_report.xlsx");
    };
    const calculateTotalHours = (key) => {
        return reportData.reduce((acc, curr) => {
            const duration = curr[key] ? parseInt(curr[key].split(' ')[0]) : 0; // Extracting the number of minutes
            return acc + duration; // Summing up all durations
        }, 0);
    };



    const formatDuration = (totalMinutes) => {
        const hours = Math.floor(totalMinutes / 60); // Calculate hours
        const minutes = totalMinutes % 60; // Remaining minutes
        return `${hours} hr ${minutes} min`; // Format as "X hr Y min"
    };

    const totals = {
        scheduledHours: formatDuration(calculateTotalHours('scheduledDuration')), // Format scheduled duration
        actualHours: formatDuration(calculateTotalHours('actualDuration')), // Format actual duration
    };
    const totalLateHours = Math.floor(totalLateMinutes / 60);
    const remainingLateMinutes = totalLateMinutes % 60;

    const fetchAuditLogs = async (memberId) => {
        const logsRef = collection(db, "memberAuditLog");
        const q = query(logsRef, where("memberId", "==", memberId));
        const snap = await getDocs(q);
        const result = snap.docs.map(doc => doc.data());
        // sort old to new
        return result.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    };
    const relevantFields = new Set([
        "membershipStartDate",
        "membershipEndDate",
        "status",
        "classesRemaining",
        "archived"
    ]);

    // Process auditLogs to combine relevant changes per timestamp + action
    const auditLogTableData = auditLogs.map((log) => {
        // Filter changes for relevant fields only
        const filteredChanges = log.changes.filter(
            (change) =>
                relevantFields.has(change.field) &&
                // Only keep if before != after (considering timestamps)
                ((change.before && change.before.seconds ? change.before.seconds : change.before) !==
                    (change.after && change.after.seconds ? change.after.seconds : change.after))
        );

        // Combine filtered changes into a single object mapping fields to {before, after}
        const combined = filteredChanges.reduce(
            (acc, change) => {
                acc[change.field] = {
                    before: change.before,
                    after: change.after,
                };
                return acc;
            },
            {
                key: String(log.timestamp.seconds || log.timestamp) + log.action,
                timestamp: log.timestamp,
                action: log.action,
                membershipStartDateBefore: null,
                membershipStartDateAfter: null,
                membershipEndDateBefore: null,
                membershipEndDateAfter: null,
                statusBefore: null,
                statusAfter: null,
                classesRemainingBefore: null,
                classesRemainingAfter: null,
            }
        );

        if (combined.membershipStartDate) {
            combined.membershipStartDateBefore = combined.membershipStartDate.before;
            combined.membershipStartDateAfter = combined.membershipStartDate.after;
        }
        if (combined.membershipEndDate) {
            combined.membershipEndDateBefore = combined.membershipEndDate.before;
            combined.membershipEndDateAfter = combined.membershipEndDate.after;
        }
        if (combined.status) {
            combined.statusBefore = combined.status.before;
            combined.statusAfter = combined.status.after;
        }
        if (combined.classesRemaining) {
            combined.classesRemainingBefore = combined.classesRemaining.before;
            combined.classesRemainingAfter = combined.classesRemaining.after;
        }
        if (combined.archived) {
            combined.archivedBefore = combined.archived.before;
            combined.archivedAfter = combined.archived.after;
        }


        return {
            ...combined,
            changedBy: log.changedBy || "Unknown",
        }
    }).filter(row =>
        // Filter out rows with no relevant changes
        row.membershipStartDateBefore !== null ||
        row.membershipEndDateBefore !== null ||
        row.statusBefore !== null ||
        row.classesRemainingBefore !== null ||
        row.archivedBefore !== null
    );

    const auditLogColumns = [
        {
            title: 'Date',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (timestamp) =>
                new Date(timestamp.seconds ? timestamp.seconds * 1000 : timestamp).toLocaleString(),
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: 120,
        },
        {
            title: 'Membership Start Date',
            key: 'membershipStartDate',
            width: 160,
            render: (_, record) => {
                const before = record.membershipStartDateBefore;
                const after = record.membershipStartDateAfter;
                if (!before && !after) {
                    return <div>-</div>;
                }
                if (before === after) {
                    return <div>-</div>;
                }
                return (
                    <div>
                        <div>From: {before ? formatDate(before) : "-"}</div>
                        <div>To: {after ? formatDate(after) : "-"}</div>
                    </div>
                );
            },
        },
        {
            title: 'Membership End Date',
            key: 'membershipEndDate',
            width: 160,
            render: (_, record) => {
                const before = record.membershipEndDateBefore;
                const after = record.membershipEndDateAfter;
                if (!before && !after) {
                    return <div>-</div>;
                }
                if (before === after) {
                    return <div>-</div>;
                }
                return (
                    <div>
                        <div>From: {before ? formatDate(before) : "-"}</div>
                        <div>To: {after ? formatDate(after) : "-"}</div>
                    </div>
                );
            },
        },
        {
            title: 'Status',
            key: 'status',
            width: 140,
            render: (_, record) => {
                const before = record.statusBefore;
                const after = record.statusAfter;
                if (!before && !after) {
                    return <div>-</div>;
                }
                if (before === after) {
                    return <div>-</div>;
                }
                return (
                    <div>
                        <div>From: {before ?? "-"}</div>
                        <div>To: {after ?? "-"}</div>
                    </div>
                );
            },
        },
        {
            title: 'Classes Remaining',
            key: 'classesRemaining',
            width: 140,
            render: (_, record) => {
                const before = record.classesRemainingBefore;
                const after = record.classesRemainingAfter;
                if (!before && !after) {
                    return <div>-</div>;
                }
                if (before === after) {
                    return <div>-</div>;
                }
                return (
                    <div>
                        <div>From: {before ?? "-"}</div>
                        <div>To: {after ?? "-"}</div>
                    </div>
                );
            },
        },
        // {
        //     title: 'Archived',
        //     key: 'archived',
        //     width: 120,
        //     render: (_, record) => (
        //         <div></div>
        //     ),
        // },
        {
            title: "Changed By",
            dataIndex: "changedBy",
            key: "changedBy",
            width: 190,
            render: (changedBy) =>
                <span style={{ fontWeight: 500 }}>{changedBy || "-"}</span>
        },
    ];

    const formatDate = (raw) => {
        if (!raw) return "-";
        if (raw.seconds) return new Date(raw.seconds * 1000).toLocaleDateString();
        if (raw.toDate) return raw.toDate().toLocaleDateString();
        if (raw instanceof Date) return raw.toLocaleDateString();
        if (typeof raw === "string" || typeof raw === "number") return new Date(raw).toLocaleDateString();
        return String(raw);
    };

    return (
        <LoadingOverlay isLoading={loading}>
            <div className={`p-4 sm:p-6 min-h-screen ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
            }`}>
                <div>
                    {/* <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg p-6 mb-6">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Member Report</h1>
                        <p className="text-blue-100 text-sm">View detailed attendance and membership history</p>
                    </div> */}

                    <div className={`rounded-lg shadow-md p-4 sm:p-6 mb-6 ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-white'
                    }`}>
                        {/* <h2 className="text-lg font-semibold text-gray-800 mb-4">Report Filters</h2> */}
                        <div className="flex flex-col md:flex-row gap-4 md:items-end">

                    {/* <Select
                        className="w-full md:w-64"
                        showSearch  // Enable search
                        placeholder="Select Member"
                        optionFilterProp="children"
                        onChange={handleMemberSelect}
                        value={selectedMember}
                        filterSort={(optionA, optionB) =>
                            (optionA?.children ?? '').toLowerCase().localeCompare((optionB?.children ?? '').toLowerCase())
                        }
                    >
                        {members.map(member => (
                            <Option key={member.id} value={member.id}>{member.name}{member.lastName}</Option>
                        ))}
                    </Select> */}
                        <div className="flex-1 min-w-[200px]">
                            <label className={`block text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-100' : 'text-gray-700'
                            }`}>Member</label>
                            <Select
                                className={`w-full ${theme === 'dark' ? 'dark-select' : ''}`}
                                dropdownClassName={theme === 'dark' ? 'dark-select-dropdown' : ''}
                                showSearch
                                placeholder="Select Member"
                                optionFilterProp="children"
                                onChange={handleMemberSelect}
                                value={selectedMember}
                                size="large"
                                filterSort={(optionA, optionB) => {
                                    const textA = String(optionA?.children ?? '').toLowerCase();
                                    const textB = String(optionB?.children ?? '').toLowerCase();
                                    return textA.localeCompare(textB);
                                }}
                            >
                                {members.map(member => (
                                    <Option key={member.id} value={member.id}>
                                        {member.name} {member.lastName}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <div className="flex-1 min-w-[180px]">
                            <label className={`block text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-100' : 'text-gray-700'
                            }`}>Start Date</label>
                            <DatePicker
                                placeholder="Select Start Date"
                                onChange={handleStartDateChange}
                                format="YYYY-MM-DD"
                                value={startDate}
                                size="large"
                                className={`w-full ${theme === 'dark' ? 'dark-datepicker' : ''}`}
                            />
                        </div>

                        <div className="flex-1 min-w-[180px]">
                            <label className={`block text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-100' : 'text-gray-700'
                            }`}>End Date</label>
                            <DatePicker
                                placeholder="Select End Date"
                                onChange={handleEndDateChange}
                                format="YYYY-MM-DD"
                                value={endDate}
                                size="large"
                                className={`w-full ${theme === 'dark' ? 'dark-datepicker' : ''}`}
                            />
                        </div>

                        <Button
                            type="primary"
                            onClick={generateReport}
                            loading={loading}
                            size="large"
                            className="bg-blue-600 hover:bg-blue-700 h-11 px-8 font-semibold"
                        >
                            Generate Report
                        </Button>
                    </div>
                </div>

                <div className={`rounded-lg shadow-md p-4 sm:p-6 mb-6 ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-white'
                }`}>
                    <div className="mb-6">
                        <h2 className={`text-xl font-bold mb-1 ${
                            theme === 'dark' ? 'text-gray-100' : 'text-gray-800'
                        }`}>Member Name: {selectedMemberName}</h2>
                        <p className={`text-sm ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                            { `Period: ${formattedDateRange}`}
                        </p>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <Table
                            columns={columns}
                            dataSource={reportData}
                            pagination={{ 
                                position: ['bottomCenter'], 
                                pageSize: 10
                            }}
                            scroll={{ x: 'max-content' }}
                            size="middle"
                            bordered
                            className={theme === 'dark' ? 'dark-table' : ''}
                            summary={() => (
                                <Table.Summary>
                                    <Table.Summary.Row className={theme === 'dark' ? 'bg-gray-600' : 'bg-blue-50'}>
                                        <Table.Summary.Cell index={0} colSpan={4}>
                                            <strong className={theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}>Total</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={4}>
                                            <strong className={theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}>{totals.scheduledHours}</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={5} colSpan={2}></Table.Summary.Cell>
                                        <Table.Summary.Cell index={7}>
                                            <strong className={theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}>{totals.actualHours}</strong>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            )}
                        />
                    </div>
                    
                    {reportData.length > 0 && (
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Button 
                                onClick={exportToExcel}
                                size="large"
                                className="bg-green-600 hover:bg-green-700 text-white border-0 font-semibold flex items-center gap-2"
                            >
                                <FaFileExcel /> Export to Excel
                            </Button>
                            <Button 
                                onClick={exportToPDF}
                                size="large"
                                className="bg-red-600 hover:bg-red-700 text-white border-0 font-semibold flex items-center gap-2"
                            >
                                <FaFilePdf /> Export to PDF
                            </Button>
                        </div>
                    )}
                </div>
                <div className={`rounded-lg shadow-md p-4 sm:p-6 mb-6 ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-white'
                }`}>
                    <div className="mb-4">
                        <h2 className={`text-xl font-bold mb-1 ${
                            theme === 'dark' ? 'text-gray-100' : 'text-gray-800'
                        }`}>Change History</h2>
                        <p className={`text-sm ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>Track all membership modifications and updates</p>
                    </div>
                    <div className="overflow-x-auto">
                        <Table
                            columns={auditLogColumns}
                            dataSource={auditLogTableData}
                            pagination={{ 
                                position: ['bottomCenter'], 
                                pageSize: 5
                            }}
                            size="middle"
                            bordered
                            scroll={{ x: 'max-content' }}
                            className={theme === 'dark' ? 'dark-table' : ''}
                        />
                    </div>
                </div>
                </div>
            </div>
        </LoadingOverlay>
    );
};

export default MemberReport;
